import React, {lazy} from 'react';
import PropTypes from 'prop-types';
import {Route, IndexRoute, IndexRedirect, Redirect} from 'react-router';
import classnames from 'classnames';

import {requireSessionWithLoginPrompt} from 'lib/decorators/requireSession';

import sessionStore from 'client/Session/SessionStore';

import SecondaryHeader from './shared/SecondaryHeader';

const Classrooms = lazy(() => import('client/Classrooms/Classrooms.react'));
const ClassroomsList = lazy(
  () => import('client/Classrooms/Teacher/ClassroomsList/ClassroomsList.react')
);
const Classroom = lazy(() => import('client/Classrooms/Classroom/Classroom.react'));

const TeacherDashboard = lazy(
  () => import('client/Classrooms/Classroom/Teacher/TeacherDashboard/TeacherDashboard')
);

const People = lazy(() => import('client/Classrooms/Classroom/Teacher/People/People.react'));
const Coteachers = lazy(
  () => import('client/Classrooms/Classroom/Teacher/People/Coteachers/Coteachers.react')
);

const ManageRoster = lazy(
  () => import('client/Classrooms/Classroom/Teacher/ManageRoster/ManageRoster.react')
);
const EnrolledStudents = lazy(
  () =>
    import('client/Classrooms/Classroom/Teacher/ManageRoster/EnrolledStudents/EnrolledStudents.react')
);
const InvitedStudents = lazy(
  () =>
    import('client/Classrooms/Classroom/Teacher/ManageRoster/InvitedStudents/InvitedStudents.react')
);
const TeacherClassroomSettings = lazy(
  () =>
    import('client/Classrooms/Classroom/Teacher/TeacherClassroomSettings/TeacherClassroomSettings.react')
);

// @todo make separate student list components for Active Assignments and Past Assignments
const AssignmentsList = lazy(
  () =>
    import('client/Classrooms/Classroom/Teacher/Assignments/AssignmentsList/AssignmentsList.react')
);

const StudentAssignmentsList = lazy(
  () => import('client/Classrooms/Classroom/Student/Assignments/StudentAssignmentsListV2')
);

const TeacherSubjectsList = lazy(
  () => import('client/Classrooms/Classroom/Teacher/Subjects/TeacherSubjectsList.react')
);

const SingleStudentView = lazy(
  () => import('client/Classrooms/Classroom/Teacher/Student/SingleStudentView.react')
);
const StudentOverview = lazy(
  () => import('client/Classrooms/Classroom/Teacher/Student/Overview/StudentOverview.react')
);
const StudentAssignmentOverview = lazy(
  () => import('client/Classrooms/Classroom/Teacher/Student/Assignments/AssignmentOverview')
);
const StudentTopics = lazy(
  () => import('client/Classrooms/Classroom/Teacher/Student/Topics/Topics')
);
const StudentInfo = lazy(
  () => import('client/Classrooms/Classroom/Teacher/Student/Info/StudentInfo.react')
);
const ApprovalStatuses = lazy(() => import('./Teacher/ApprovalStatuses/ApprovalStatuses.react'));

function requireTeacherStatus(nextState, replace) {
  if (!sessionStore.hasTeacherAccess()) {
    replace('/', null);
  }
}

function renderComponentWithStatuses(Component) {
  function ComponentWithStatuses(props) {
    return (
      <>
        <ApprovalStatuses
          className={classnames('max-w-[1280px]', {
            'max-w-[1088px]': props.route.path === 'insights' || props.route.path === 'overview'
          })}
          type='neutral'
        />
        <Component {...props} />
      </>
    );
  }

  ComponentWithStatuses.propTypes = {
    route: PropTypes.object
  };

  return ComponentWithStatuses;
}

const classroomSessionRequired = requireSessionWithLoginPrompt()(({children, ...rest}) => {
  return (
    <>
      <SecondaryHeader {...rest} aria-label='secondary' />
      <Classrooms {...rest}>{children}</Classrooms>
    </>
  );
});

export default (
  <Route
    title='Classes | Albert.io'
    path='classes'
    component={classroomSessionRequired}
    onEnter={(nextState, replace, callback) => {
      if (sessionStore.isTeacher()) {
        callback?.();
        return;
      } else {
        replace('/home');
        callback?.();
      }
    }}
  >
    <IndexRoute component={renderComponentWithStatuses(ClassroomsList)} withWhiteBackground />
    <Route
      path='archived'
      onEnter={requireTeacherStatus}
      component={renderComponentWithStatuses(ClassroomsList)}
      title='Archived'
      withWhiteBackground
    />
    {
      /**
       * @todo – When we use `archive_date` to derive whether or not a classroom is archived we will be able to
       * allow users to edit the date.
       * @see https://github.com/albert-io/project-management/milestone/49
       */
      // <Route
      //   path='auto-archive-list'
      //   onEnter={requireTeacherStatus}
      //   component={AutoArchiveClassroomsList}
      // />
    }
    <Route path=':classId' component={Classroom}>
      <Route path='my-assignments' component={StudentAssignmentsList} />

      <Route
        path='assignments'
        onEnter={requireTeacherStatus}
        component={renderComponentWithStatuses(AssignmentsList)}
      >
        <Redirect from='templates' to='/saved' />
      </Route>
      {/*
        Legacy redirects for assessments and exams
        these may be unnecessary at this point,
        but it doesn't hurt to keep them in case any users still use these URLs
      */}
      <Redirect from='my-exams' to='my-assignments' />
      <Redirect from='my-assessments' to='my-assignments' />
      <Redirect from='assessments' to='assignments' />
      <Redirect from='exams' to='assignments' />
      {/* Legacy redirects end */}
      <Route
        path='people'
        onEnter={requireTeacherStatus}
        component={renderComponentWithStatuses(People)}
        withWhiteBackground
      >
        <IndexRedirect to='students/enrolled' />
        <Route component={ManageRoster}>
          <Route
            path='students/enrolled'
            component={EnrolledStudents}
            title='Enrolled'
            withWhiteBackground
          />
          <Route
            path='students/invited'
            component={InvitedStudents}
            title='Invited'
            withWhiteBackground
          />
        </Route>
        <Route
          path='coteachers/approved'
          component={Coteachers}
          title='Coteachers'
          withWhiteBackground
        />
      </Route>

      <Route
        path='subjects'
        component={renderComponentWithStatuses(TeacherSubjectsList)}
        onEnter={requireTeacherStatus}
        withWhiteBackground
      />

      <Route path='students' onEnter={requireTeacherStatus}>
        <IndexRedirect to='overview' />
        <Route path='overview' component={renderComponentWithStatuses(TeacherDashboard)} />

        <Route path='detail/:userId' component={SingleStudentView}>
          <IndexRedirect to='overview' />
          <Route path='info' component={StudentInfo} />
          <Route path='assignments' component={StudentAssignmentOverview} />
          <Route path='overview' component={StudentOverview} />
          <Route path='topics' component={StudentTopics} />
        </Route>
      </Route>
      <Route path='settings' onEnter={requireTeacherStatus}>
        <IndexRedirect to='details' />
        <Route path='details' component={renderComponentWithStatuses(TeacherClassroomSettings)} />
      </Route>
    </Route>
  </Route>
);
